/*
  <product-carousel> — paged, scroll-snap carousel.
  Native horizontal scrolling handles touch/trackpad; arrows move one page,
  dots reflect (and jump to) the current page. Page size is measured from the
  layout, so CSS alone decides how many slides are visible per breakpoint.

  Markup hooks: [data-carousel-track], [data-carousel-prev], [data-carousel-next], [data-carousel-dots]
  Options (data attributes): data-loop="true", data-autoplay="true", data-speed="5" (seconds)
*/
if (!customElements.get('product-carousel')) {
  class ProductCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-carousel-track]');
      if (!this.track) return;

      this.slides = Array.from(this.track.children);
      this.prevButton = this.querySelector('[data-carousel-prev]');
      this.nextButton = this.querySelector('[data-carousel-next]');
      this.dotsContainer = this.querySelector('[data-carousel-dots]');
      this.loop = this.dataset.loop === 'true';
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.prevButton?.addEventListener('click', () => this.step(-1));
      this.nextButton?.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', () => this.scheduleUpdate(), { passive: true });

      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);

      if (this.dataset.autoplay === 'true' && !this.reducedMotion.matches) this.setUpAutoplay();
      this.update();
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      clearInterval(this.autoplayTimer);
    }

    get slideStep() {
      if (this.slides.length < 2) return this.track.clientWidth;
      return this.slides[1].offsetLeft - this.slides[0].offsetLeft;
    }

    get perPage() {
      return Math.max(1, Math.floor((this.track.clientWidth + 1) / this.slideStep));
    }

    get pageCount() {
      return Math.max(1, Math.ceil(this.slides.length / this.perPage));
    }

    get maxScroll() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    get currentPage() {
      if (this.track.scrollLeft >= this.maxScroll - 2) return this.pageCount - 1;
      return Math.round(this.track.scrollLeft / (this.perPage * this.slideStep));
    }

    step(direction) {
      let page = this.currentPage + direction;
      if (page < 0) page = this.loop ? this.pageCount - 1 : 0;
      if (page >= this.pageCount) page = this.loop ? 0 : this.pageCount - 1;
      this.goTo(page);
    }

    goTo(page) {
      const left = Math.min(page * this.perPage * this.slideStep, this.maxScroll);
      this.track.scrollTo({ left, behavior: this.reducedMotion.matches ? 'auto' : 'smooth' });
    }

    scheduleUpdate() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateState();
      });
    }

    update() {
      this.renderDots();
      this.updateState();
    }

    renderDots() {
      const count = this.pageCount;
      this.classList.toggle('product-carousel--static', count <= 1);
      if (!this.dotsContainer || this.dotsContainer.childElementCount === count) return;

      const label = this.dotsContainer.dataset.label || '[page]';
      this.dotsContainer.replaceChildren(
        ...Array.from({ length: count }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'product-carousel__dot';
          dot.setAttribute('aria-label', label.replace('[page]', index + 1));
          dot.addEventListener('click', () => this.goTo(index));
          return dot;
        })
      );
    }

    updateState() {
      const current = this.currentPage;
      this.dotsContainer?.querySelectorAll('button').forEach((dot, index) => {
        dot.setAttribute('aria-current', index === current);
      });

      if (this.loop) return;
      if (this.prevButton) this.prevButton.disabled = current === 0;
      if (this.nextButton) this.nextButton.disabled = current === this.pageCount - 1;
    }

    setUpAutoplay() {
      const speed = (parseInt(this.dataset.speed, 10) || 5) * 1000;
      const start = () => {
        clearInterval(this.autoplayTimer);
        this.autoplayTimer = setInterval(() => this.step(1), speed);
      };
      const stop = () => clearInterval(this.autoplayTimer);

      this.addEventListener('mouseenter', stop);
      this.addEventListener('mouseleave', start);
      this.addEventListener('focusin', stop);
      this.addEventListener('focusout', start);
      start();
    }
  }

  customElements.define('product-carousel', ProductCarousel);
}
